/**
 * @fileoverview Toolbar with search, grade range segmented control, and category filter
 */

import { memo, useState, useEffect, useMemo } from 'react'
import type { FC } from 'react'
import { useTranslation } from 'react-i18next'
import { SegmentedControl, Select, Tooltip } from '@mantine/core'
import type { MantineTheme } from '@mantine/core'
import { Stack, Group, TextSearch, Text } from 'components'
import { StatusChips } from './StatusChips'
import type { DisplayStatus, GradeRange } from '../types'

interface GradeOption {
  value: string
  label: string
}

interface CategoryOption {
  value: string
  label: string
}

interface ProjectsToolbarProps {
  search: string
  status: DisplayStatus
  showStatusChips: boolean
  showGradeFilter?: boolean
  showCategoryFilter?: boolean
  grades?: GradeOption[]
  gradeRange?: GradeRange
  categories?: CategoryOption[]
  categoryId?: string
  excludeStatuses?: DisplayStatus[]
  onSearchChange: (value: string) => void
  onStatusChange: (value: DisplayStatus) => void
  onGradeRangeChange?: (value: GradeRange) => void
  onCategoryChange?: (value: string) => void
}

const DEBOUNCE_MS = 300

const GRADE_RANGE_OPTIONS: { value: GradeRange; label: string }[] = [
  { value: 'all', label: 'arc_projects.grade_all' },
  { value: 'k-2', label: 'PK-2' },
  { value: '3-5', label: '3-5' },
  { value: '6-8', label: '6-8' },
  { value: '9-12', label: '9-12' },
]

const getSegmentedStyles = (theme: MantineTheme) => ({
  root: {
    backgroundColor: theme.colorScheme === 'dark'
      ? theme.colors.dark[5]
      : theme.colors.gray[3],
  },
  control: {
    border: 'none !important',
  },
  indicator: {
    backgroundColor: theme.colors.teal[6],
  },
  label: {
    color: theme.colorScheme === 'dark' ? theme.colors.dark[1] : theme.colors.gray[7],
    '&[data-active]': {
      color: theme.white,
    },
  },
})

export const ProjectsToolbar: FC<ProjectsToolbarProps> = memo(({
  search,
  status,
  showStatusChips,
  showGradeFilter = false,
  showCategoryFilter = false,
  grades = [],
  gradeRange = 'all',
  categories = [],
  categoryId = '',
  excludeStatuses = [],
  onSearchChange,
  onStatusChange,
  onGradeRangeChange,
  onCategoryChange,
}) => {
  const { t } = useTranslation()
  const [localSearch, setLocalSearch] = useState(search)

  useEffect(() => {
    setLocalSearch(search)
  }, [search])

  useEffect(() => {
    const timer = setTimeout(() => {
      if (localSearch !== search) {
        onSearchChange(localSearch)
      }
    }, DEBOUNCE_MS)

    return () => clearTimeout(timer)
  }, [localSearch, search, onSearchChange])

  const gradeRangeData = useMemo(
    () => GRADE_RANGE_OPTIONS.map(opt => ({
      value: opt.value,
      label: opt.value === 'all' ? t(opt.label) : opt.label,
    })),
    [t],
  )

  const categoryOptions = useMemo(
    () => [{ value: '', label: t('arc_projects.all_categories') }, ...categories],
    [t, categories],
  )

  const hasCategories = categories.length > 0

  return (
    <Stack spacing="md">
      <TextSearch
        placeholder={t('arc_projects.search_placeholder')}
        size="lg"
        onChange={setLocalSearch}
      />

      {(showStatusChips || showGradeFilter) && (
        <Group position={showStatusChips ? 'apart' : 'left'}>
          {showStatusChips && (
            <StatusChips value={status} onChange={onStatusChange} excludeStatuses={excludeStatuses} />
          )}
          {showGradeFilter && (
            <Group spacing="md" noWrap>
              {showCategoryFilter && (
                <Group spacing="xs" noWrap>
                  <Text size="sm" weight={600} color="dimmed" style={{ whiteSpace: 'nowrap' }}>
                    {t('arc_projects.category_label')}
                  </Text>
                  <Tooltip
                    label={t('arc_projects.no_categories_available')}
                    disabled={hasCategories}
                    withArrow
                  >
                    <div>
                      <Select
                        data={categoryOptions}
                        value={categoryId}
                        onChange={(value) => onCategoryChange?.(value || '')}
                        clearable={false}
                        disabled={!hasCategories}
                        size="sm"
                        styles={{ input: { minWidth: 160 } }}
                      />
                    </div>
                  </Tooltip>
                </Group>
              )}
              <Group spacing="xs" noWrap>
                <Text size="sm" weight={600} color="dimmed" style={{ whiteSpace: 'nowrap' }}>
                  {t('arc_projects.grades_label')}
                </Text>
                <SegmentedControl
                  value={gradeRange}
                  onChange={(value) => onGradeRangeChange?.(value as GradeRange)}
                  data={gradeRangeData}
                  size="sm"
                  radius="xl"
                  styles={getSegmentedStyles}
                />
              </Group>
            </Group>
          )}
        </Group>
      )}
    </Stack>
  )
})

ProjectsToolbar.displayName = 'ProjectsToolbar'
